import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectors, fetchAssetOverlay, REAL_TIME_SCENARIO } from 'state';
import SplitBodyV3 from './AssetPositioningSplitV3';
import type { RelativizeDate } from '../../helpers';

/**
 * "Positioning vs market" — the Drivers tile in the `--half-right` slot. Overlays
 * the clicked instrument's price (market truth, top) with the player's signed
 * positioning (exposure columns, bottom). Rendering is the hand-rolled SVG
 * `SplitBodyV3`; this wrapper handles selection, the data fetch, and the
 * loading / empty / no-instrument-selected states.
 *
 * Data: `GET /sessions/:id/fetch_asset_overlay` (price = as-of mid, exposure =
 * sign(amount) × reporting_value, sampled at the session's own step dates);
 * historical sessions only on the backend.
 */

export type SelectedInstrument = {
  financial_instrument_id?: number;
  underlying_asset_id?: number | null;
  name: string;
};

type Point = {
  price: number | null;
  exposure: number; // signed reporting-ccy exposure
  label: string; // ISO date
};

const PANEL_LABEL: React.CSSProperties = {
  color: 'var(--s2-muted)',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.02em',
};

const Centered: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      flex: '1 1 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      padding: 16,
      ...PANEL_LABEL,
      fontWeight: 500,
    }}
  >
    {children}
  </div>
);

const AssetPositioningTile: React.FC<{
  playerId: number;
  selected: SelectedInstrument | null;
  // Self + historical only: relativises the overlay tooltip dates to "Day N".
  relativizeDate?: RelativizeDate;
}> = ({ playerId, selected, relativizeDate }) => {
  const dispatch = useDispatch();
  const session = useSelector(selectors.activeSession);
  const sessionId = session?.id;
  const overlay = session?.asset_overlay;
  const fiId = selected?.financial_instrument_id;
  const isRealSession = sessionId != null;
  // Real-time bars are intraday → the tooltip shows date + HH:MM (historical stays date-only).
  const isRealTime = session?.scenario_type === REAL_TIME_SCENARIO;

  useEffect(() => {
    if (isRealSession && sessionId != null && fiId != null) {
      dispatch(
        fetchAssetOverlay({
          session_id: sessionId,
          player_id: playerId,
          financial_instrument_id: fiId,
        }),
      );
    }
  }, [dispatch, isRealSession, sessionId, playerId, fiId]);

  // Resolve what to render: the fetched overlay for the selected instrument, or
  // a loading / empty / no-instrument-selected message.
  let body: React.ReactNode;
  let title: string;
  if (fiId == null) {
    body = (
      <Centered>Click an instrument to compare positioning vs market</Centered>
    );
    title = '—';
  } else {
    const data = overlay?.data;
    const matches = data?.financial_instrument_id === fiId;
    title =
      (matches ? data?.asset_name : selected?.name) || selected?.name || '';
    if (!matches) {
      body = <Centered>Loading…</Centered>;
    } else if (!data || data.series.length === 0) {
      body = <Centered>No price/position history for this instrument</Centered>;
    } else {
      const points: Point[] = data.series.map(s => ({
        price: s.price,
        exposure: s.exposure,
        label: s.timestamp,
      }));
      body = (
        <SplitBodyV3
          points={points}
          relativizeDate={relativizeDate}
          intraday={isRealTime}
        />
      );
    }
  }

  return (
    <div className="studio2__card studio2__card--half-right">
      <div className="studio2__card-head">
        <span className="studio2__card-title">
          Positioning vs market{' '}
          <span
            className="s2-muted"
            style={{ fontWeight: 500, textTransform: 'none' }}
          >
            — {title}
          </span>
        </span>
      </div>
      <div
        className="studio2__card-body"
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          gap: 2,
        }}
      >
        {body}
      </div>
    </div>
  );
};

export default AssetPositioningTile;
